import React, { useMemo } from 'react';

import { InfoCircleIcon } from '@cloudquery/base-icons';
import SettingsIcon from '@mui/icons-material/Settings';
import Button from '@mui/material/Button';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import Popover from '@mui/material/Popover';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import Tooltip from '@mui/material/Tooltip';
import { GridColDef } from '@mui/x-data-grid';

import { AssetInventoryGridColDef, DisplayColumns } from '../../types';

export function ColumnToggle({
  columns,
  displayColumns,
  overwriteDisplayColumns,
  resetDisplayColumns,
  setDisplayColumns,
}: {
  columns: GridColDef[];
  displayColumns: DisplayColumns;
  overwriteDisplayColumns: (newColumns: DisplayColumns) => void;
  resetDisplayColumns: () => void;
  setDisplayColumns: (field: string) => void;
}) {
  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(null);

  const allSelected = useMemo(
    () => columns.length === displayColumns.size,
    [columns, displayColumns],
  );

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const id = open ? 'column-selector' : undefined;

  return (
    <>
      <IconButton data-testid="column-toggle-button" onClick={handleClick}>
        <SettingsIcon />
      </IconButton>
      <Popover
        anchorEl={anchorEl}
        anchorOrigin={{
          horizontal: 'center',
          vertical: 'bottom',
        }}
        id={id}
        onClick={(e) => {
          e.stopPropagation();
        }}
        onClose={handleClose}
        open={open}
        slotProps={{
          paper: {
            sx: {
              maxHeight: '70vh',
              minWidth: '300px',
            },
          },
        }}
      >
        <Stack gap={1} padding={1.5}>
          <Stack direction="row" gap={1}>
            <Button
              fullWidth={true}
              onClick={() => {
                if (allSelected) {
                  overwriteDisplayColumns(new Set());
                } else {
                  overwriteDisplayColumns(new Set(columns.map(({ field }) => field)));
                }
              }}
              size="small"
              sx={{ whiteSpace: 'nowrap' }}
              variant="outlined"
            >
              {`${allSelected ? 'Hide' : 'Show'} all columns`}
            </Button>
            <Button
              fullWidth={true}
              onClick={resetDisplayColumns}
              size="small"
              sx={{ whiteSpace: 'nowrap' }}
              variant="outlined"
            >
              {`Restore defaults`}
            </Button>
          </Stack>

          {columns.map(
            ({ description, field, headerName, isCustomColumn }: AssetInventoryGridColDef) => {
              return (
                <Stack key={field} alignItems="center" direction="row">
                  <FormControlLabel
                    control={
                      <Switch
                        checked={displayColumns.has(field)}
                        onChange={() => setDisplayColumns(field)}
                      />
                    }
                    label={headerName}
                  />
                  {isCustomColumn && description && (
                    <Tooltip placement="top" title={description}>
                      <span style={{ alignItems: 'center', display: 'flex' }}>
                        <InfoCircleIcon />
                      </span>
                    </Tooltip>
                  )}
                </Stack>
              );
            },
          )}
        </Stack>
      </Popover>
    </>
  );
}
